
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';
import type { ProductWithPrice } from 'types';
import { groupChatModelFamilies } from '@/lib/chatModelPicker';
import clsxm from '@/lib/clsxm';
import { employeeModelOptions } from '@/lib/employee-validation';
import { getProductsWithPricesInArray, getTeamProductsWithPrices, } from '@/data/products';
import { tone } from '@/components/agents/primitives';
import EmployeesApps from '@/components/ai-employees-landing/EmployeesApps';
import EmployeesFaq, { type PlanLimits, } from '@/components/ai-employees-landing/EmployeesFaq';
import EmployeesFilm from '@/components/ai-employees-landing/EmployeesFilm';
import EmployeesFinalCta from '@/components/ai-employees-landing/EmployeesFinalCta';
import EmployeesHero from '@/components/ai-employees-landing/EmployeesHero';
import EmployeesHowItWorks from '@/components/ai-employees-landing/EmployeesHowItWorks';
import EmployeesPricing, { type EmployeePlan, } from '@/components/ai-employees-landing/EmployeesPricing';
import EmployeesRoster from '@/components/ai-employees-landing/EmployeesRoster';
import EmployeesTeams from '@/components/ai-employees-landing/EmployeesTeams';
import EmployeesToolkit, { type ToolkitModel, } from '@/components/ai-employees-landing/EmployeesToolkit';
import { EmployeesApprovals, EmployeesRoutines, EmployeesTasks, } from '@/components/ai-employees-landing/EmployeesWorkflow';
import { LANDING_PATH, type RosterCategory, type RosterEmployee, type TeamPreset, useEmployeesT, } from '@/components/ai-employees-landing/kit';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
import { BUILTIN_MCP_CONNECTORS } from '@/constants/builtin-mcp-connectors';
import { CHAT_MODEL_EFFORTS, type ChatModelProvider, FEATURED_CHAT_MODELS, } from '@/constants/chatModels';
import { WORDS_IN_PLAN } from '@/constants/common';
import { employeePhotoLargeUrl } from '@/constants/employeePhotos';
import { EMPLOYEE_CATEGORIES, EMPLOYEE_TEMPLATES, employeesLimitForPlan, NATIVE_CONNECTOR_TOKENS, proposeTeam, TEAM_ROLES, } from '@/constants/employees';
interface Props {
    roster: RosterEmployee[];
    categories: RosterCategory[];
    teams: TeamPreset[];
    plans: EmployeePlan[];
    teamProducts: ProductWithPrice[];
    limits: PlanLimits;
    models: ToolkitModel[];
    modelCount: number;
    appTotal: number;
}
/*
 * The AI Employees landing page. Story: hire a team of named specialists,
 * brief them like a team, and stay in charge (tasks, approvals) while they
 * work on their own (routines). Then what each hire comes with, the apps,
 * teams for each kind of work, plans and questions.
 */
export default function AiEmployees({ roster, categories, teams, plans, teamProducts, limits, models, modelCount, appTotal, }: Props) {
    const { lang } = useLocale('ai-employees');
    const { t } = useEmployeesT();
    const appCount = Math.max(10, Math.floor(appTotal / 10) * 10);
    const breadcrumbs = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Easy-Peasy.AI',
                item: 'https://easy-peasy.ai',
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: t('AI Employees'),
                item: `https://easy-peasy.ai${lang === 'en' ? '' : `/${lang}`}${LANDING_PATH}`,
            },
        ],
    };
    return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main className={clsxm('relative overflow-x-clip', tone.page)} dir={lang === 'ar' ? 'rtl' : undefined}>
      <Seo templateTitle={t('AI Employees: Hire an AI Team for Your Business')} description={t('Hire AI employees that work as a team: {{count}} ready-made roles, group chats, tasks and routines, and your approval before anything goes out. Free to start.', { count: roster.length })} image='https://media.easy-peasy.ai/landing/ai-employees/v1/og-1200x630.jpg' structuredData={false}/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
        }}/>
      </Head>

      <EmployeesHero roleCount={roster.length} appCount={appCount}/>
      <TeamsLogos compact caption={t('Trusted by teams at companies and universities like')}/>
      <EmployeesFilm />
      <EmployeesRoster roster={roster} categories={categories}/>
      <EmployeesHowItWorks />
      <EmployeesTasks />
      <EmployeesApprovals />
      <EmployeesRoutines />
      <EmployeesToolkit models={models} modelCount={modelCount}/>
      <EmployeesApps appTotal={appTotal}/>
      <EmployeesTeams teams={teams} roster={roster}/>
      <EmployeesPricing plans={plans} teamProducts={teamProducts}/>
      <EmployeesFaq limits={limits} appTotal={appTotal}/>
      <EmployeesFinalCta faces={roster.slice(0, 9).map((employee) => employee.photo)}/>
      <Footer />
    </main>);
}
const STARTER_PRODUCT = 'prod_MakjxDVwGdvoVK';
const UNLIMITED_PRODUCT = 'prod_NtlgdYiAMxIUwD';
// The providers whose newest model the toolkit lists, in this order.
const MODEL_PROVIDERS: ChatModelProvider[] = [
    'OpenAI',
    'Anthropic',
    'Google',
    'Z.ai',
];
async function getStaticProps() {
    // Everything below runs at build time, so the templates (with their long
    // instructions) and the model catalogue never reach the browser.
    const roster: RosterEmployee[] = EMPLOYEE_TEMPLATES.map((template) => ({
        slug: template.slug,
        name: template.name,
        role: template.roleTitle,
        category: template.category,
        tagline: template.tagline,
        color: template.color,
        photo: template.avatar,
        photoLarge: employeePhotoLargeUrl(template.avatar) ?? template.avatar,
        outputs: template.outputs,
        capabilities: template.capabilities
            .slice(0, 3)
            .map(({ icon, title }) => ({ icon, title })),
    }));
    const teams: TeamPreset[] = TEAM_ROLES.map((role) => ({
        id: role.id,
        emoji: role.emoji,
        label: role.label,
        description: role.description,
        members: proposeTeam([role.id], '', 5),
    }));
    let individual: ProductWithPrice[] = [];
    let teamProducts: ProductWithPrice[] = [];
    try {
        [individual, teamProducts] = await Promise.all([
            getProductsWithPricesInArray([STARTER_PRODUCT, UNLIMITED_PRODUCT]),
            getTeamProductsWithPrices(),
        ]);
    }
    catch (error) {
        // The page still renders; the paid cards show no price until the next
        // regeneration.
        console.error('[ai-employees] could not load prices', error);
    }
    const product = (id: string) => individual.find((item) => item.id === id);
    const plans: EmployeePlan[] = [
        {
            name: 'Free',
            employees: employeesLimitForPlan('Free'),
            words: WORDS_IN_PLAN.Free,
        },
        {
            name: 'Starter',
            employees: employeesLimitForPlan('Starter'),
            words: WORDS_IN_PLAN.Starter,
            product: product(STARTER_PRODUCT),
        },
        {
            name: 'Unlimited',
            employees: employeesLimitForPlan('Unlimited'),
            words: WORDS_IN_PLAN.Unlimited,
            product: product(UNLIMITED_PRODUCT),
        },
        {
            name: 'Teams',
            employees: employeesLimitForPlan('Teams'),
            words: WORDS_IN_PLAN.Teams,
        },
    ];
    // `undefined` can't be serialized into page props.
    const serializablePlans = plans.map(({ product: item, ...rest }) => item ? { ...rest, product: item } : rest);
    const limits: PlanLimits = {
        free: employeesLimitForPlan('Free'),
        starter: employeesLimitForPlan('Starter'),
        unlimited: employeesLimitForPlan('Unlimited'),
        teams: employeesLimitForPlan('Teams'),
    };
    // Employees run only on models that have a computer (the sandbox).
    const options = employeeModelOptions();
    const available = new Set(options.map((model) => model.slug));
    const models: ToolkitModel[] = MODEL_PROVIDERS.flatMap((provider) => {
        const featured = FEATURED_CHAT_MODELS[provider] ?? [];
        const pick = featured.find((item) => available.has(item.slug));
        const model = options.find((item) => item.slug === pick?.slug);
        return model ? [{ provider, title: model.title }] : [];
    });
    // Count models the way the picker lists them: effort variants of one
    // model are one row.
    const families = groupChatModelFamilies(options, CHAT_MODEL_EFFORTS);
    const modelCount = Math.max(10, Math.floor(families.length / 10) * 10);
    // Every app an employee can be connected to, one per brand (WhatsApp is
    // a customer channel, not an app the employee works in).
    const builtinBrands = new Set(BUILTIN_MCP_CONNECTORS.map((connector) => connector.name.replace(/\s*\(.*\)\s*$/, '')));
    const nativeApps = NATIVE_CONNECTOR_TOKENS.filter((token) => token !== 'whatsapp').length;
    const appTotal = nativeApps + builtinBrands.size;
    return {
        props: {
            roster,
            categories: EMPLOYEE_CATEGORIES.map(({ id, label }) => ({ id, label })),
            teams,
            plans: serializablePlans,
            teamProducts,
            limits,
            models,
            modelCount,
            appTotal,
        },
        // Prices come from Stripe products: pick up changes within the hour.
        revalidate: 3600,
    };
}

    async function __Next_Translate__getStaticProps__1a13a29a251__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-employees',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a251__ as getStaticProps }
  